// JSON-LD builders. These produce plain objects; SeoService serialises them into
// <script type="application/ld+json"> tags in the <head> during SERVER rendering, so crawlers see
// them in the initial HTML (spec §8).
//
// Rule from the spec: only emit schema that matches content actually visible on the page. In
// particular we do NOT emit Review/AggregateRating anywhere — the spec forbids fake ratings, and our
// rating data is too thin to represent honestly.

export type JsonLd = Record<string, unknown>;

export const ORGANIZATION_ID = 'https://savingarena.com/#organization';
export const WEBSITE_ID = 'https://savingarena.com/#website';

/**
 * The brand's own social profiles, for `Organization.sameAs`.
 *
 * This is how a search engine ties the site to the accounts that carry the same name — it is the
 * evidence behind a knowledge panel. Every URL here is a live, verified account; a profile that
 * 404s is worse than an absent one, because it is a claim the crawler can check and disprove.
 */
const SOCIAL_PROFILES = [
  'https://www.facebook.com/Savingarena',
  'https://x.com/savingarena',
  'https://www.instagram.com/savingarena/',
  'https://www.tiktok.com/@savingarena',
  'https://www.youtube.com/@savingarena8586',
];

export function organizationSchema(origin: string): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${origin}/#organization`,
    name: 'SavingArena',
    url: `${origin}/`,
    // Square, and it has to actually exist — this used to point at a file that was never in the
    // build, so the one image Google fetches for the brand was a 404 on every page.
    logo: `${origin}/images/logo.png`,
    sameAs: SOCIAL_PROFILES,
    description:
      'SavingArena publishes verified coupon codes, discounts and deals from thousands of online stores.',
  };
}

export function websiteSchema(origin: string): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    name: 'SavingArena',
    url: `${origin}/`,
    publisher: { '@id': `${origin}/#organization` },
    // NO `potentialAction`/SearchAction here on purpose. A sitelinks-searchbox action must point at a
    // real results URL, and this app has no `/search` route — the header search is a client-side
    // dropdown, and robots.txt disallows `/search` anyway. Advertising a searchbox that 404s is
    // invalid structured data, so it stays out until a crawlable search results page exists.
  };
}

export interface Crumb {
  name: string;
  /** Absolute URL. Omit on the final crumb (the current page). */
  url?: string;
}

export function breadcrumbSchema(crumbs: Crumb[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      ...(crumb.url ? { item: crumb.url } : {}),
    })),
  };
}

export function webPageSchema(input: {
  origin: string;
  url: string;
  name: string;
  description?: string;
  dateModified?: string;
}): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: input.url,
    name: input.name,
    ...(input.description ? { description: input.description } : {}),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
    isPartOf: { '@id': `${input.origin}/#website` },
    publisher: { '@id': `${input.origin}/#organization` },
  };
}

/**
 * An Offer for a coupon/deal. `seller` is the store the offer belongs to.
 *
 * We deliberately omit `price` — a coupon is a discount, not a priced product, and inventing a price
 * would be structured-data spam. Discount is conveyed through the name/description.
 */
export function offerSchema(input: {
  name: string;
  description?: string;
  url: string;
  storeName: string;
  validThrough?: string;
}): JsonLd {
  return {
    '@type': 'Offer',
    name: input.name,
    ...(input.description ? { description: input.description } : {}),
    url: input.url,
    ...(input.validThrough ? { validThrough: input.validThrough } : {}),
    seller: { '@type': 'Organization', name: input.storeName },
  };
}

/** A list of offers/entities. Used on store, category and comparison pages. */
export function itemListSchema(items: JsonLd[], name?: string): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    ...(name ? { name } : {}),
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item,
    })),
  };
}

/**
 * FAQPage. Callers MUST only emit this when the FAQs are actually rendered on the page — Google
 * penalises FAQ schema for content the user cannot see (spec §8).
 */
export function faqPageSchema(faqs: { question: string; answer: string }[]): JsonLd | null {
  const valid = faqs.filter((faq) => faq.question?.trim() && faq.answer?.trim());
  if (!valid.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: valid.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}

export function articleSchema(input: {
  origin: string;
  url: string;
  headline: string;
  description?: string;
  image?: string;
  authorName?: string;
  datePublished?: string;
  dateModified?: string;
}): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': input.url },
    headline: input.headline,
    ...(input.description ? { description: input.description } : {}),
    ...(input.image ? { image: input.image } : {}),
    ...(input.authorName ? { author: { '@type': 'Person', name: input.authorName } } : {}),
    ...(input.datePublished ? { datePublished: input.datePublished } : {}),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
    publisher: { '@id': `${input.origin}/#organization` },
  };
}
